import * as Yup from 'yup'
import {CatalogConfig, CatalogFieldConfig, CatalogItem} from '../types'

// Builds a Yup schema from a catalog config, mirroring the API constraints (required, max
// length, URL format). Uniqueness (e.g. beneficiary `name`) is enforced by the backend and
// surfaced through handleApiError on the 400 response — it cannot be checked client-side.
//
// Takes a plain translate function rather than an IntlShape so it stays pure and unit-testable.

export type Translate = (id: string, values?: Record<string, string | number>) => string

// Optional fields come back from the form as '' — normalize to undefined so that an empty
// optional URL/text passes instead of failing url()/format checks.
const emptyToUndefined = (value: unknown, originalValue: unknown) =>
  originalValue === '' ? undefined : value

const buildFieldSchema = (field: CatalogFieldConfig, t: Translate): Yup.StringSchema => {
  let schema = Yup.string().transform(emptyToUndefined)

  if (field.type === 'url') {
    schema = schema.url(t('CATALOG.VALIDATION.URL'))
  }

  if (field.maxLength) {
    schema = schema.max(field.maxLength, t('CATALOG.VALIDATION.MAX', {max: field.maxLength}))
  }

  if (field.required) {
    schema = schema.required(t('CATALOG.VALIDATION.REQUIRED', {field: t(field.labelId)}))
  } else {
    schema = schema.notRequired()
  }

  return schema
}

export const buildValidationSchema = (config: CatalogConfig, t: Translate) => {
  const shape: Record<string, Yup.StringSchema> = {}
  config.fields.forEach((field) => {
    shape[field.name] = buildFieldSchema(field, t)
  })
  return Yup.object(shape)
}

// Initial Formik values for the form: empty strings when creating, existing values when editing.
export const buildInitialValues = (
  config: CatalogConfig,
  item?: CatalogItem
): Record<string, string> => {
  const values: Record<string, string> = {}
  config.fields.forEach((field) => {
    const raw = item ? item[field.name] : ''
    values[field.name] = raw === null || raw === undefined ? '' : String(raw)
  })
  return values
}
